import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { TextStdRg, Typography } from '@/UI/AppTypography/AppTypography';
import { AppDialogSection, AppDialog, AppDialogContent } from '@/UI/AppDialog/AppDialog';
import { Box } from '@/UI/Box/Box';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { FlexCol } from '@/UI/styles';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { FPL_ICON_COLOR_VAR } from '@/UI/Icons/fplIcon';
import { Link } from '@/UI/Link/Link';
import { I18N_KEYS } from '@/language/keys';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

export function TermsOfServiceDialog({ onAccept }: { onAccept: () => void }) {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  return (
    <AppDialog
      open
      fullWidth
      width={400}
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            '&&': {
              border: 'none',
              borderRadius: '13px',
            },
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogContent sx={{ p: isFplMigrationEnabled ? 3 : 4 }}>
          {/* Weavy logo */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: color.White100,
              border: `2px solid ${color.Black92}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: isFplMigrationEnabled ? 1 : 2,
              // The glyph sits on a white circle, so it has to be dark. FPL icons take their color
              // from the surrounding context rather than a prop, so the circle declares it.
              ...(isFplMigrationEnabled && { [FPL_ICON_COLOR_VAR]: color.Black100 }),
            }}
          >
            <WeavyLogoFilled
              width={28}
              height={24}
              style={isFplMigrationEnabled ? undefined : { color: color.Black100 }}
            />
          </Box>

          <FlexCol sx={{ mb: isFplMigrationEnabled ? 3 : 4 }}>
            {/* Title */}
            <Typography variant="body-lg-sb" sx={{ color: token.text, mb: isFplMigrationEnabled ? 0.5 : 1 }}>
              {t(I18N_KEYS.SIGN_IN_PAGE.TOS_DIALOG_TITLE)}
            </Typography>

            {/* Body */}
            <TextStdRg sx={{ alignSelf: 'stretch' }}>
              {t(I18N_KEYS.SIGN_IN_PAGE.TOS_DIALOG_BODY)}{' '}
              <Link
                href={EXTERNAL_LINKS.termsOfService}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: token.textSecondary, textDecorationColor: token.textSecondary }}
              >
                {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_OF_SERVICE_LINK)}
              </Link>{' '}
              and{' '}
              <Link
                href={EXTERNAL_LINKS.privacyPolicy}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: token.textSecondary, textDecorationColor: token.textSecondary }}
              >
                {t(I18N_KEYS.SIGN_IN_PAGE.PRIVACY_POLICY_LINK)}
              </Link>
              .
            </TextStdRg>
          </FlexCol>

          <ButtonContained onClick={onAccept} fullWidth>
            {t(I18N_KEYS.SIGN_IN_PAGE.TOS_DIALOG_ACCEPT_BUTTON)}
          </ButtonContained>
        </AppDialogContent>
      </AppDialogSection>
    </AppDialog>
  );
}
